import React, { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../api";
import { useLiveData } from "../live";
import { BarChart } from "../components/Charts";
import { downloadPath } from "../download";

const errMsg = (e) => e?.response?.data?.message || e?.message || "Request failed";
const bytes = (n) => { n = Number(n || 0); const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0; while (n >= 1024 && i < 4) { n /= 1024; i++; } return `${n.toFixed(i ? 1 : 0)} ${u[i]}`; };
const ago = (d) => {
  if (!d) return "never";
  const s = Math.max(0, (Date.now() - new Date(d)) / 1000);
  return s < 60 ? "just now" : s < 3600 ? `${Math.round(s / 60)} min ago` : s < 86400 ? `${Math.round(s / 3600)} h ago` : `${Math.round(s / 86400)} d ago`;
};
const seen = (m) => [m.last_heartbeat_at, m.last_seen_at].filter(Boolean).sort().pop();
const TONE = { Online: "#047857", Idle: "#1d4ed8", Stale: "#b45309", Offline: "#b91c1c" };

const K = ({ n, l, tone }) => <div className={`kpi ${tone || ""}`}><div className="n">{n ?? 0}</div><div className="l">{l}</div></div>;
const LiveBadge = ({ live }) => <span className="pill" style={{ color: live ? "#047857" : "#64748b" }}>{live ? "● Live" : "○ Polling"}</span>;
const State = ({ s }) => <span className="pill" style={{ color: TONE[s] || "#334155", fontWeight: 600 }}>{s}</span>;
const Enc = ({ e }) => {
  const c = e.encryption_status === "ENCRYPTED" ? "#047857" : e.encryption_status === "UNENCRYPTED" ? "#b91c1c" : "#64748b";
  return <span title={e.encryption_format || ""} style={{ color: c, fontWeight: 600 }}>{e.encryption_status}{e.encryption_scope ? ` · ${e.encryption_scope}` : ""}{e.encryption_format ? ` · ${e.encryption_format}` : ""}</span>;
};

function Pager({ page, pageSize, total, set }) {
  const last = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="toolbar" style={{ justifyContent: "space-between", borderTop: "1px solid var(--line)", borderBottom: "none" }}>
      <span>{total} total</span>
      <span><button className="btn sm" disabled={page <= 1} onClick={() => set(page - 1)}>Prev</button> Page {page} / {last} <button className="btn sm" disabled={page >= last} onClick={() => set(page + 1)}>Next</button></span>
    </div>
  );
}

/* ── Overview ─────────────────────────────────────────────────────────────── */
export function EstateOverview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/estate/summary").then((r) => { setD(r.data?.data); setErr(""); }).catch((e) => setErr(errMsg(e))), []);
  const live = useLiveData(load);
  const s = d?.states || {};
  const p = d?.personalByProtection || {};
  return (
    <div>
      <h1>Discovery Overview <LiveBadge live={live} /></h1>
      <p className="sub">Personal data found by the agents, and how it is protected</p>
      {err && <div className="err">{err}</div>}
      <div className="kpis">
        <K n={d?.machines} l="Machines" />
        <K n={s.Online} l="Online now" tone="ok" />
        <K n={(s.Stale || 0) + (s.Offline || 0)} l="Stale / offline" tone={(s.Stale || 0) + (s.Offline || 0) ? "warn" : ""} />
        <K n={d?.scans} l="Scans" />
        <K n={d?.items} l="Files reported" />
        <K n={p.unencrypted} l="Unencrypted personal data" tone="danger" />
        <K n={p.fileEncrypted} l="Personal data, file/column-encrypted" tone="ok" />
        <K n={p.volumeEncrypted} l="Personal data, disk/TDE-encrypted only" tone="warn" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 16 }}>
        <BarChart title="Unencrypted files by personal-data type" data={d?.unencryptedByType} empty="No unencrypted personal data found" />
        <div className="card">
          <div className="toolbar"><strong>Machines with most unencrypted personal data</strong></div>
          <table><tbody>
            {(d?.topMachines || []).map((m) => <tr key={m.id}><td><Link to={`/machines/${m.id}`}>{m.hostname || m.id}</Link></td><td style={{ textAlign: "right" }}>{m.files} files</td></tr>)}
            {!d?.topMachines?.length && <tr><td className="sub" style={{ padding: 16 }}>None</td></tr>}
          </tbody></table>
        </div>
        <div className="card">
          <div className="toolbar"><strong>Agent Servers</strong><Link to="/agent-servers" style={{ marginLeft: "auto" }}>Manage</Link></div>
          <table><tbody>
            {(d?.agentServers || []).map((a) => (
              <tr key={a.id}><td>{a.name}</td><td>{a.version || "—"}</td><td>{ago(a.last_seen_at)}</td>
                <td><State s={a.status === "Revoked" ? "Offline" : a.online ? "Online" : "Offline"} /></td></tr>
            ))}
            {!d?.agentServers?.length && <tr><td className="sub" style={{ padding: 16 }}>No Agent Server enrolled</td></tr>}
          </tbody></table>
          {d?.agentServers?.some((a) => !a.online && a.status === "Active") && <div className="err" style={{ margin: 12 }}>An Agent Server is offline: machine data may be delayed.</div>}
        </div>
      </div>
    </div>
  );
}

/* ── Machines ─────────────────────────────────────────────────────────────── */
export function Machines() {
  const [res, setRes] = useState({ rows: [], total: 0 });
  const [f, setF] = useState({ q: "", state: "", page: 1 });
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/estate/machines", { params: { q: f.q || undefined, state: f.state || undefined, page: f.page, pageSize: 50 } })
    .then((r) => { setRes(r.data?.data || { rows: [], total: 0 }); setErr(""); }).catch((e) => setErr(errMsg(e))), [f]);
  const live = useLiveData(load);
  return (
    <div>
      <h1>Machines <LiveBadge live={live} /></h1>
      {err && <div className="err">{err}</div>}
      <div className="card">
        <div className="toolbar">
          <input placeholder="Search host, OS, id…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} />
          <select value={f.state} onChange={(e) => setF({ ...f, state: e.target.value, page: 1 })}>
            <option value="">All states</option>{["Online", "Idle", "Stale", "Offline"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <table>
          <thead><tr><th>Host</th><th>OS</th><th>Type</th><th>IP</th><th>MAC</th><th>Disk enc.</th><th>State</th><th>Last seen</th><th>Files</th><th>Unenc. personal</th></tr></thead>
          <tbody>
            {res.rows.map((m) => (
              <tr key={m.id}>
                <td><Link to={`/machines/${m.id}`}>{m.hostname || m.machine_uid}</Link></td><td>{m.os} {m.os_version ? <span className="sub">{m.os_version}</span> : null}</td>
                <td>{m.machine_type || "—"}</td><td>{m.ips || "—"}</td><td>{m.macs || "—"}</td><td>{m.disk_encryption || "—"}</td>
                <td><State s={m.state} /></td><td>{ago(seen(m))}</td><td>{m.items}</td>
                <td style={{ color: m.unencrypted_personal ? "#b91c1c" : undefined, fontWeight: m.unencrypted_personal ? 700 : 400 }}>{m.unencrypted_personal}</td>
              </tr>
            ))}
            {!res.rows.length && <tr><td colSpan={10} style={{ textAlign: "center", padding: 24 }}>No machines yet</td></tr>}
          </tbody>
        </table>
        <Pager page={f.page} pageSize={50} total={res.total} set={(page) => setF({ ...f, page })} />
      </div>
    </div>
  );
}

export function MachineDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/estate/machines/${id}`).then((r) => { setM(r.data?.data); setErr(""); }).catch((e) => setErr(errMsg(e))), [id]);
  const live = useLiveData(load);
  if (err) return <div><Link to="/machines">← Machines</Link><div className="err">{err}</div></div>;
  if (!m) return <p className="sub">Loading…</p>;
  return (
    <div>
      <Link to="/machines">← Machines</Link>
      <h1>{m.hostname || m.machine_uid} <State s={m.state} /> <LiveBadge live={live} /></h1>
      <div className="kpis">
        <K n={m.os} l={m.os_version || "OS"} /><K n={m.machine_type || "—"} l="Type" /><K n={m.disk_encryption || "—"} l="Disk encryption" />
        <K n={m.agent_version || "—"} l="Agent version" /><K n={ago(seen(m))} l="Last seen" /><K n={ago(m.last_scan_at)} l="Last scan" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 16, marginBottom: 16 }}>
        <div className="card"><div className="toolbar"><strong>Network</strong></div>
          <table><thead><tr><th>Adapter</th><th>MAC</th><th>IP addresses</th></tr></thead><tbody>
            {m.nics.map((n) => <tr key={n.id}><td>{n.name}</td><td>{n.mac}</td><td>{(n.ip_addresses || []).join(", ")}</td></tr>)}
            {!m.nics.length && <tr><td colSpan={3} className="sub" style={{ padding: 12 }}>No adapters reported</td></tr>}
          </tbody></table></div>
        <div className="card"><div className="toolbar"><strong>Recent scans</strong></div>
          <table><thead><tr><th>Started</th><th>Status</th><th>Files</th><th>Findings</th></tr></thead><tbody>
            {m.scans.map((s) => <tr key={s.id}><td>{ago(s.started_at)}</td><td>{s.status}</td><td>{s.files_scanned}</td><td><Link to={`/findings?scan_id=${s.id}`}>{s.items_found}</Link></td></tr>)}
          </tbody></table></div>
      </div>
      <h3>Findings on this machine</h3>
      <Findings fixed={{ machine_id: id }} compact />
    </div>
  );
}

/* ── Scans ────────────────────────────────────────────────────────────────── */
export function EstateScans() {
  const [res, setRes] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(1);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/estate/scans", { params: { page, pageSize: 50 } }).then((r) => { setRes(r.data?.data || { rows: [], total: 0 }); setErr(""); }).catch((e) => setErr(errMsg(e))), [page]);
  const live = useLiveData(load);
  return (
    <div>
      <h1>Scans <LiveBadge live={live} /></h1>
      {err && <div className="err">{err}</div>}
      <div className="card">
        <table>
          <thead><tr><th>Machine</th><th>Status</th><th>Started</th><th>Finished</th><th>Files scanned</th><th>Data read</th><th>Findings</th><th>Note</th></tr></thead>
          <tbody>
            {res.rows.map((s) => (
              <tr key={s.id}><td><Link to={`/machines/${s.machine_id}`}>{s.hostname}</Link></td><td>{s.status}</td><td>{s.started_at ? new Date(s.started_at).toLocaleString() : "—"}</td>
                <td>{s.finished_at ? new Date(s.finished_at).toLocaleString() : "—"}</td><td>{s.files_scanned}</td><td>{bytes(s.bytes_scanned)}</td>
                <td><Link to={`/findings?scan_id=${s.id}`}>{s.items_found}</Link></td><td className="sub">{s.error || ""}</td></tr>
            ))}
            {!res.rows.length && <tr><td colSpan={8} style={{ textAlign: "center", padding: 24 }}>No scans yet</td></tr>}
          </tbody>
        </table>
        <Pager page={page} pageSize={50} total={res.total} set={setPage} />
      </div>
    </div>
  );
}

/* ── Files & Findings (also used for "Unencrypted Personal Data") ─────────── */
export function Findings({ fixed = {}, compact = false, preset = {}, title = "Files & Findings" }) {
  const qs = new URLSearchParams(window.location.search);
  const [f, setF] = useState({ q: "", encryption: qs.get("encryption") || preset.encryption || "", scope: "", risk: "", pii: preset.pii ?? false, scan_id: qs.get("scan_id") || "", page: 1 });
  const [res, setRes] = useState({ rows: [], total: 0 });
  const [err, setErr] = useState("");
  const params = useCallback(() => ({ ...fixed, q: f.q || undefined, encryption: f.encryption || undefined, scope: f.scope || undefined, risk: f.risk || undefined,
    has_pii: f.pii ? "1" : undefined, scan_id: f.scan_id || undefined }), [f, fixed]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => api.get("/estate/items", { params: { ...params(), page: f.page, pageSize: 50 } })
    .then((r) => { setRes(r.data?.data || { rows: [], total: 0 }); setErr(""); }).catch((e) => setErr(errMsg(e))), [f, params]);
  const live = useLiveData(load);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value, page: 1 });
  return (
    <div>
      {!compact && <h1>{title} <LiveBadge live={live} /></h1>}
      {err && <div className="err">{err}</div>}
      <div className="card">
        <div className="toolbar" style={{ flexWrap: "wrap" }}>
          <input placeholder="Search file name or path…" value={f.q} onChange={set("q")} />
          <select value={f.encryption} onChange={set("encryption")}><option value="">Any encryption</option><option>UNENCRYPTED</option><option>ENCRYPTED</option><option>UNKNOWN</option></select>
          <select value={f.scope} onChange={set("scope")}><option value="">Any scope</option><option value="file">file</option><option value="column">column</option><option value="volume">volume</option><option value="database">database</option></select>
          <select value={f.risk} onChange={set("risk")}><option value="">Any risk</option><option>High</option><option>Medium</option><option>Low</option></select>
          <label><input type="checkbox" checked={f.pii} onChange={set("pii")} /> Personal data only</label>
          <button className="btn sm" style={{ marginLeft: "auto" }} onClick={() => downloadPath("/estate/items.csv", "findings.csv", params()).catch((e) => setErr(errMsg(e)))}>Export CSV</button>
        </div>
        <table>
          <thead><tr>{!compact && <th>Machine</th>}<th>File</th><th>Path</th><th>Size</th><th>Encryption</th><th>Data types</th><th>Matches</th><th>Risk</th><th>Scanned</th></tr></thead>
          <tbody>
            {res.rows.map((r) => (
              <tr key={r.id}>
                {!compact && <td><Link to={`/machines/${r.machine_id}`}>{r.hostname}</Link></td>}
                <td>{r.file_name}</td><td title={r.file_path} style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.file_path}</td>
                <td>{bytes(r.size_bytes)}</td><td><Enc e={r} /></td>
                <td>{(r.data_types || []).map((t) => <span key={t} className="pill" style={{ marginRight: 4 }}>{t}</span>)}</td>
                <td>{r.match_count}</td><td>{r.risk}</td><td>{ago(r.scanned_at)}</td>
              </tr>
            ))}
            {!res.rows.length && <tr><td colSpan={9} style={{ textAlign: "center", padding: 24 }}>No findings</td></tr>}
          </tbody>
        </table>
        <Pager page={f.page} pageSize={50} total={res.total} set={(page) => setF({ ...f, page })} />
      </div>
    </div>
  );
}

export function Unencrypted() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/estate/unencrypted/by-machine").then((r) => { setRows(r.data?.data || []); setErr(""); }).catch((e) => setErr(errMsg(e))), []);
  const live = useLiveData(load);
  return (
    <div>
      <h1>Unencrypted Personal Data <LiveBadge live={live} /></h1>
      <p className="sub">Files holding personal data that are not encrypted at file level or by disk encryption. Evidence for DPDP Rule 6 (security safeguards).</p>
      {err && <div className="err">{err}</div>}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="toolbar"><strong>By machine</strong></div>
        <table>
          <thead><tr><th>Machine</th><th>OS</th><th>State</th><th>Files</th><th>High-risk files</th><th>Matches</th></tr></thead>
          <tbody>
            {rows.map((r) => <tr key={r.id}><td><Link to={`/machines/${r.id}`}>{r.hostname}</Link></td><td>{r.os}</td><td><State s={r.state} /></td><td>{r.files}</td><td style={{ color: r.high_risk ? "#b91c1c" : undefined }}>{r.high_risk}</td><td>{r.matches}</td></tr>)}
            {!rows.length && <tr><td colSpan={6} style={{ textAlign: "center", padding: 24 }}>No unencrypted personal data found</td></tr>}
          </tbody>
        </table>
      </div>
      <Findings preset={{ encryption: "UNENCRYPTED", pii: true }} title="Files" compact={false} />
    </div>
  );
}


